"use client";

import { c as _c } from "react/compiler-runtime";
import { Tooltip as TooltipPrimitive } from "radix-ui";
import { tv } from "tailwind-variants";
import type { VariantProps } from "tailwind-variants";

// --- Tooltip Provider --- //

type TooltipProviderProps = React.ComponentProps<typeof TooltipPrimitive.Provider>;
function BaseTooltipProvider(t0) {
  const $ = _c(6);
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      delayDuration: t1,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = props;
    $[2] = t1;
  } else {
    props = $[1];
    t1 = $[2];
  }
  const delayDuration = t1 === undefined ? 0 : t1;
  let t2;
  if ($[3] !== delayDuration || $[4] !== props) {
    t2 = <TooltipPrimitive.Provider data-slot="tooltip-provider" delayDuration={delayDuration} {...props} />;
    $[3] = delayDuration;
    $[4] = props;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
}

// --- Tooltip --- //

type TooltipProps = React.ComponentProps<typeof TooltipPrimitive.Root>;
function BaseTooltip(t0) {
  const $ = _c(4);
  let props;
  if ($[0] !== t0) {
    ({
      ...props
    } = t0);
    $[0] = t0;
    $[1] = props;
  } else {
    props = $[1];
  }
  let t1;
  if ($[2] !== props) {
    t1 = <BaseTooltipProvider><TooltipPrimitive.Root data-slot="tooltip" {...props} /></BaseTooltipProvider>;
    $[2] = props;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
}

// --- Tooltip Trigger --- //

type TooltipTriggerProps = React.ComponentProps<typeof TooltipPrimitive.Trigger>;
function BaseTooltipTrigger(t0) {
  const $ = _c(4);
  let props;
  if ($[0] !== t0) {
    ({
      ...props
    } = t0);
    $[0] = t0;
    $[1] = props;
  } else {
    props = $[1];
  }
  let t1;
  if ($[2] !== props) {
    t1 = <TooltipPrimitive.Trigger data-slot="tooltip-trigger" {...props} />;
    $[2] = props;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
}

// --- Tooltip Content --- //

const tooltipContent = tv({
  slots: {
    base: `
      animate-in
      fade-in-0
      zoom-in-95
      data-[state=closed]:animate-out
      data-[state=closed]:fade-out-0
      data-[state=closed]:zoom-out-95
      data-[side=bottom]:slide-in-from-top-2
      data-[side=left]:slide-in-from-right-2
      data-[side=right]:slide-in-from-left-2
      data-[side=top]:slide-in-from-bottom-2
      z-50
      w-fit
      origin-[--radix-tooltip-content-transform-origin]
      rounded-sm
      text-sm
    `,
    arrow: `
      z-50
      size-2.5
      -translate-y-1/2
      rotate-45
      rounded-[2px]
      border-r
      border-b
    `
  },
  variants: {
    variant: {
      light: {
        base: `
          bg-background-primary
          text-content-primary
          border
          border-border-secondary
          shadow-popover
        `,
        arrow: `
          bg-background-primary
          fill-background-primary
          border-border-secondary
        `
      },
      dark: {
        base: `
          bg-primary
          text-primary-foreground
        `,
        arrow: `
          bg-primary
          fill-primary
        `
      }
    },
    padding: {
      sm: `
        px-3
        py-1.5
      `,
      md: `
        py-4
        px-4
      `
    },
    width: {
      xs: {
        base: `
          max-w-xs
        `
      },
      sm: {
        base: `
          max-w-sm
        `
      },
      md: {
        base: `
          max-w-md
        `
      },
      lg: {
        base: `
          max-w-lg
        `
      },
      xl: {
        base: `
          max-w-xl
        `
      },
      fit: {
        base: `
          max-w-fit
        `
      }
    }
  },
  defaultVariants: {
    variant: "light",
    padding: "md",
    width: "sm"
  }
});
type TooltipContentProps = React.ComponentProps<typeof TooltipPrimitive.Content> & VariantProps<typeof tooltipContent> & {
  arrow?: boolean;
};
function BaseTooltipContent(t0) {
  const $ = _c(36);
  let children;
  let className;
  let props;
  let t1;
  let t2;
  let t3;
  let t4;
  if ($[0] !== t0) {
    ({
      className,
      children,
      sideOffset: t1,
      arrow: t2,
      width: t3,
      padding: t4,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
    t1 = $[4];
    t2 = $[5];
    t3 = $[6];
    t4 = $[7];
  }
  const sideOffset = t1 === undefined ? 0 : t1;
  const arrow = t2 === undefined ? true : t2;
  const width = t3 === undefined ? "sm" : t3;
  const padding = t4 === undefined ? "md" : t4;
  let T0;
  let T1;
  let t10;
  let t11;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[8] !== arrow || $[9] !== children || $[10] !== className || $[11] !== padding || $[12] !== props || $[13] !== sideOffset || $[14] !== width) {
    const {
      base,
      arrow: arrowClass
    } = tooltipContent({
      className,
      width,
      padding
    });
    T1 = TooltipPrimitive.Portal;
    T0 = TooltipPrimitive.Content;
    t5 = base();
    t6 = 6;
    t7 = "tooltip-content";
    t8 = sideOffset;
    t9 = props;
    t10 = children;
    t11 = arrow && <TooltipPrimitive.Arrow className={arrowClass()} />;
    $[8] = arrow;
    $[9] = children;
    $[10] = className;
    $[11] = padding;
    $[12] = props;
    $[13] = sideOffset;
    $[14] = width;
    $[15] = T0;
    $[16] = T1;
    $[17] = t10;
    $[18] = t11;
    $[19] = t5;
    $[20] = t6;
    $[21] = t7;
    $[22] = t8;
    $[23] = t9;
  } else {
    T0 = $[15];
    T1 = $[16];
    t10 = $[17];
    t11 = $[18];
    t5 = $[19];
    t6 = $[20];
    t7 = $[21];
    t8 = $[22];
    t9 = $[23];
  }
  let t12;
  if ($[24] !== T0 || $[25] !== t10 || $[26] !== t11 || $[27] !== t5 || $[28] !== t6 || $[29] !== t7 || $[30] !== t8 || $[31] !== t9) {
    t12 = <T0 className={t5} collisionPadding={t6} data-slot={t7} sideOffset={t8} {...t9}>{t10}{t11}</T0>;
    $[24] = T0;
    $[25] = t10;
    $[26] = t11;
    $[27] = t5;
    $[28] = t6;
    $[29] = t7;
    $[30] = t8;
    $[31] = t9;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  let t13;
  if ($[33] !== T1 || $[34] !== t12) {
    t13 = <T1>{t12}</T1>;
    $[33] = T1;
    $[34] = t12;
    $[35] = t13;
  } else {
    t13 = $[35];
  }
  return t13;
}
export { BaseTooltip as Tooltip, BaseTooltipTrigger as TooltipTrigger, BaseTooltipContent as TooltipContent, BaseTooltipProvider as TooltipProvider };
export type { TooltipProps, TooltipTriggerProps, TooltipContentProps, TooltipProviderProps };